import type { ParserSource } from './parser';
import { Parser } from './parser';

// Note: This is pretty much the exact same functionality as
// @mimir/common/transcoding/nut/bufferedStream except that it operates
// with Uint8Arrays instead of Buffers
export class MultiArrayBuffer implements ParserSource {
  public push(buffer: Uint8Array) {
    this.buffers.push(buffer);
    this.end += buffer.length;
  }

  public lock(callback: (parser: Parser) => void): boolean {
    const parser = new Parser(this);

    // There is no point in trying to read again if the data which was
    // requested the previous attempt as not been provided yet.
    // This is an optimization to avoid O(n²) when reading large byte
    // arrays out of the ParserSource
    if (this.end < this.blockUntil) {
      return false;
    }

    try {
      callback(parser);
      this.commit();
      return true;
    } catch (error: any) {
      this.rollback();
      if (isMultiArrayNeedsMoreDataError(error)) {
        return false;
      }
      throw error;
    }
  }

  // End of public API

  blockUntil: number = 0;

  end: number = 0;

  buffers: Uint8Array[] = [];
  marked: MultiArrayOffset = {
    bufferOffset: 0,
    bufferIndex: 0,
    absoluteOffset: 0,
  };
  position: MultiArrayOffset = {
    bufferOffset: 0,
    bufferIndex: 0,
    absoluteOffset: 0,
  };

  commit() {
    this.buffers = this.buffers.splice(this.position.bufferIndex);
    this.position.bufferIndex = 0;
    this.marked = { ...this.position };
  }

  rollback() {
    this.position = { ...this.marked };
  }

  // ParserSource implementation

  /**
   * Creates a source that is limited to a subsection of the main source. This
   * will also move the read head of the main source forward. This operation
   * can be rolled back as well, but rolling back will not affect the created
   * sub parser
   */
  subSource(size: number): ParserSource {
    const sub = new MultiArrayBuffer();
    while (size > 0) {
      if (this.position.bufferIndex >= this.buffers.length) {
        this.blockUntil = this.position.absoluteOffset + size;
        this.needsMoreData();
      }
      const buffer = this.buffers[this.position.bufferIndex];
      if (this.position.bufferOffset === buffer.length) {
        this.position.bufferIndex += 1;
        this.position.bufferOffset = 0;
        continue;
      }

      const subarray = buffer.subarray(this.position.bufferOffset);
      const clamped = subarray.subarray(0, size);
      this.position.bufferOffset += clamped.length;
      size -= clamped.length;
      sub.buffers.push(clamped);
    }
    return sub;
  }

  remain(): number {
    return this.end - this.position.absoluteOffset;
  }

  readUint8(): number {
    while (true) {
      if (this.position.bufferIndex >= this.buffers.length) {
        this.needsMoreData();
      }
      const buffer = this.buffers[this.position.bufferIndex];
      if (this.position.bufferOffset >= buffer.length) {
        this.position.bufferIndex += 1;
        this.position.bufferOffset = 0;
        continue;
      }
      const byte = buffer[this.position.bufferOffset];
      this.position.bufferOffset += 1;
      this.position.absoluteOffset += 1;
      return byte;
    }
  }

  readUint8Array(size: number): Uint8Array {
    if (this.remain() < size) {
      this.blockUntil = this.position.absoluteOffset + size;
      this.needsMoreData();
    }

    const chunks : Uint8Array[] = [];
    let needed = size;
    while (needed > 0) {
      const buffer = this.buffers[this.position.bufferIndex];
      const remainInBuffer = buffer.length - this.position.bufferOffset;
      const advance = Math.min(remainInBuffer, needed);
      chunks.push(buffer.subarray(this.position.bufferOffset, this.position.bufferOffset + advance));
      needed -= advance;
      this.position.bufferOffset += advance;
      this.position.absoluteOffset += advance;

      if (this.position.bufferOffset === buffer.length) {
        this.position.bufferIndex += 1;
        this.position.bufferOffset = 0;
      }
    }
    if (chunks.length === 1) {
      return chunks[0];
    } else {
      const outputs = new Uint8Array(size);
      let offset = 0;
      for (const chunk of chunks) {
        outputs.set(chunk, offset);
        offset += chunk.length;
      }
      return outputs;
    }
  }

  needsMoreData(): never {
    throw {
      type: 'multiArrayNeedsMoreData',
      toString: () => 'Unexpected end of multi array buffer',
    };
  }
}

interface MultiArrayOffset {
  bufferIndex: number,
  bufferOffset: number,
  absoluteOffset: number,
}

export interface MultiArrayNeedsMoreDataError {
  type: 'multiArrayNeedsMoreData',
}

function isMultiArrayNeedsMoreDataError(error: any): boolean {
  return (error.type === 'multiArrayNeedsMoreData');
}

